<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Create New Video File</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Upload Video File</h3>
        <div v-if="isSuperuser()" class="form-group full-width">
          <label for="channel" class="required">Channel</label>
          <select 
            id="channel"
            v-model="channelId"
            class="form-control"
            :disabled="videoFileId"
            required
          >
            <option value="">Select a channel</option>
            <option v-for="channel in sortedChannels" :key="channel.id" :value="channel.id">
              {{ channel.name }}
            </option>
          </select>
        </div>

        <div v-if="channelId" class="form-group full-width">
          <label for="video-upload" class="required">Video File</label>
          <VideoUploadField
            ref="videoUploadField"
            v-model="videoFileId"
            :channel-id="channelId"
            folder="distributor_content"
            :max-file-size="50 * 1024 * 1024 * 1024"
          />
        </div>
        
        <div class="form-group half-width">
          <label for="format" class="required">Format</label>
          <select
            id="format"
            v-model="formData.format"
            class="form-control"
            required
          >
            <option value="">Select a format</option>
            <option
              v-for="option in formatOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.display }}
            </option>
          </select>
        </div>
        
        <div class="form-group half-width">
          <label for="file_nickname">File Nickname</label>
          <input
            type="text"
            id="file_nickname"
            v-model="formData.file_nickname"
            class="form-control"
            placeholder="Optional"
            maxlength="80"
          >
        </div>
      </div>
      
      <div class="form-actions">
        <button 
          type="submit" 
          class="submit-button big"
          :disabled="!isFormValid"
        >
          Save & Encode
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import VideoUploadField from '@/components/VideoUploadField.vue';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';
import { formatOptions } from '@/scripts/shared-video';
import { getAllChannels, channelKeys } from '@/scripts/api/channelApi';
import { updateVideoFile, videoFileKeys } from '@/scripts/api/videoFileApi';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const videoFileId = ref(null);
const channelId = ref('');
const formData = ref({
  format: '',
  file_nickname: ''
});

// Fetch channels if the user is a superuser
const { data: channels } = useQuery({
  queryKey: channelKeys.getAllChannels(),
  queryFn: getAllChannels,
  enabled: isSuperuser(),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching channels'),
});

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => a.name.localeCompare(b.name));
});

const isFormValid = computed(() => {
  return channelId.value && videoFileId.value && formData.value.format;
});

const { mutate: updateFile } = useMutation({
  mutationFn: (data) => updateVideoFile(videoFileId.value, data),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: videoFileKeys.getAllVideoFiles() });
    queryClient.invalidateQueries({ queryKey: channelKeys.getChannelVideoFiles(channelId.value) });
    closeForm();
  },
  onError: (err) => {
    handleApiError(err, 'updating video file');
    alert('Error saving video file. Please try again.');
  },
});

const handleSubmit = () => {
  if (!isFormValid.value) return;

  const updateData = {
    format: formData.value.format,
  };

  if (formData.value.file_nickname) {
    updateData.file_nickname = formData.value.file_nickname;
  }

  updateFile(updateData);
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/videofiles' : '/filemanager');
};

onMounted(() => {
  if (route.params.channel_id) {
    channelId.value = route.params.channel_id;
  }
});
</script>

<style scoped>
@import '../styles/shared-form.css';
</style>
